import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Logo } from '@/components/common/Logo';
import { ProfileMenu } from '@/components/common/ProfileMenu';
import { useIsAdmin } from '@/api/hooks/useAuth';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { navItems } from './navItems';
import { NewProjectButton } from './NewProjectButton';

const itemClasses = (active: boolean) =>
  cn(
    'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
    active
      ? 'bg-sidebar-primary/10 text-sidebar-primary'
      : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
  );

const childClasses = (active: boolean) =>
  cn(
    'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors',
    active
      ? 'bg-sidebar-primary/10 text-sidebar-primary font-medium'
      : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
  );

/**
 * Mobile navigation: a hamburger button (hidden at `md` and up, where the
 * desktop sidebar takes over) that opens a slide-in drawer mirroring the
 * shared `navItems` tree. The drawer closes on navigation.
 */
export function MobileNav() {
  const { t } = useTranslation('common');
  const isAdmin = useIsAdmin();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const visibleItems = navItems.filter((item) => !item.adminOnly || isAdmin);
  const close = () => setOpen(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          aria-label={t('nav.openMenu')}
        >
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="left"
        className="w-72 max-w-[85vw] gap-0 bg-sidebar p-0 text-sidebar-foreground"
      >
        <SheetTitle className="sr-only">{t('nav.menu')}</SheetTitle>
        <div className="flex h-16 items-center border-b border-sidebar-border/60 px-4">
          <Logo size="lg" />
        </div>

        <div className="px-3 pt-4">
          <NewProjectButton onNavigate={close} />
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {visibleItems.map((item) => {
            const sectionActive = location.pathname.startsWith(item.to);
            return (
              <div key={item.to} className={item.children ? 'space-y-0.5' : undefined}>
                <NavLink
                  to={item.to}
                  end={!item.children}
                  onClick={close}
                  className={({ isActive }) =>
                    itemClasses(item.children ? sectionActive : isActive)
                  }
                >
                  <item.icon className="h-4 w-4" />
                  <span className="flex-1">{t(item.labelKey)}</span>
                </NavLink>
                {item.children && (
                  <div className="ml-3 space-y-0.5 border-l border-sidebar-border/60 pl-3">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        onClick={close}
                        className={({ isActive }) => childClasses(isActive)}
                      >
                        <child.icon className="h-3.5 w-3.5" />
                        <span>{t(child.labelKey)}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-sidebar-border px-2 py-3">
          <ProfileMenu collapsed={false} />
        </div>
      </SheetContent>
    </Sheet>
  );
}
